import { ActiveCell, DetectionMode } from '@EcamModel/model/PMC8266EventMotionDetection';
import { Divider, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import { SectionsCollapsePMC8266 } from '@pages/cameras/hook/useMilesightPMC8266';
import color from '@theme/Colors';
import { ReactNode } from 'react';
import { transformScheduleData } from 'src/helpers';
import {
    DataSectionHasSubItem,
    SectionHasSubItemHeader,
    SectionHasSubItemProps,
    SectionNotSubItem,
} from '../../../ptz/milesight/components/BasicInfoMilesightCameraPTZ';

const activeCellSettingMapping: { [key in ActiveCell]: string } = {
    [ActiveCell.Normal]: 'Normal',
    [ActiveCell.Compatible]: 'Compatible',
};

const detectionModeMapping: { [key in DetectionMode]: string } = {
    [DetectionMode.Normal]: 'Normal',
    [DetectionMode.Advanced]: 'Advanced',
};

type Props = {
    isOpenCollapse: { [key in SectionsCollapsePMC8266]: boolean };
    handleToggleCollapse: (section: SectionsCollapsePMC8266) => void;
};

export default function MotionDetectionPMC8266(props: Props) {
    const handleEdit = () => {
        console.log('Edit button clicked');
    };

    const { camera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;

    const PMC8266EventMotionDetection = camera?.PMC8266Camera?.PMC8266CameraConfigs?.EventMotionDetection;

    const statusList_MotionDetectionHeader: DataSectionHasSubItem[] = [
        {
            label: 'Enable detection:',
            value: <Typography>{`${PMC8266EventMotionDetection?.DynamicAnalysisEnable! ? 'Yes' : 'No'}`}</Typography>,
        },
        {
            label: 'Enable motion analysis:',
            value: <Typography>{`${PMC8266EventMotionDetection?.MotionEnable! ? 'Yes' : 'No'}`}</Typography>,
        },
    ];

    const statusList_MotionDetection_BasicSettings: DataSectionHasSubItem[] = [
        {
            label: 'Mode:',
            value: <Typography>{detectionModeMapping[PMC8266EventMotionDetection?.DetectionMode!]}</Typography>,
        },
        {
            label: 'Sensitivity:',
            value: <Typography>{PMC8266EventMotionDetection?.TriggerSensitivity!}</Typography>,
        },
        {
            label: 'Onvif Motion ActiveCells Settings:',
            value: <Typography>{activeCellSettingMapping[PMC8266EventMotionDetection?.Activecell!]}</Typography>,
        },
    ];

    const dataSectionHasSubItems: ReactNode | DataSectionHasSubItem[] = transformScheduleData(
        PMC8266EventMotionDetection?.ScheduleList!,
        (dayName, schedule) => ({
            label: `${dayName}:`,
            value: schedule,
        })
    );

    const statusList_MotionDetection_ScheduleSettings: SectionHasSubItemProps = {
        title: '',
        data: dataSectionHasSubItems,
    };

    return (
        <Stack id="motionDetection_basicSettings" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            {/* Basic Information */}
            <SectionHasSubItemHeader id="" title="Motion detection" onEdit={handleEdit} />

            <Divider
                orientation="vertical"
                sx={{
                    borderBottomWidth: 1,
                    borderColor: '#E4E7EC',
                    mx: 2,
                }}
            />

            <Stack width={'100%'} padding={'16px 16px'} bgcolor={color.white} gap={2}>
                {statusList_MotionDetectionHeader.map((item, index) => {
                    return (
                        <Grid key={index} xs={12} container>
                            <Grid xs={5}>
                                <Typography>{item.label}</Typography>
                            </Grid>
                            <Grid xs={7}>{item.value}</Grid>
                        </Grid>
                    );
                })}
            </Stack>

            <SectionNotSubItem
                header="Basic Settings"
                statusList={statusList_MotionDetection_BasicSettings}
                isOpen={isOpenCollapse.motionDetection_basicSettings}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePMC8266.MotionDetectionBasicSettings);
                }}
            />

            <SectionNotSubItem
                name="motionDetection_scheduleSettings"
                header="Schedule Settings"
                statusList={statusList_MotionDetection_ScheduleSettings.data}
                isOpen={isOpenCollapse.motionDetection_scheduleSettings}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePMC8266.MotionDetectionScheduleSettings);
                }}
            />
        </Stack>
    );
}
